import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const STATUS_STYLE = {
  present: { bg: "bg-[#00ff90]/15 border-[#00ff90]/30",  text: "text-[#00ff90]"   },
  absent:  { bg: "bg-red-500/15 border-red-500/30",       text: "text-red-400"     },
  leave:   { bg: "bg-yellow-500/15 border-yellow-500/30", text: "text-yellow-400"  },
  wfh:     { bg: "bg-blue-500/15 border-blue-500/30",     text: "text-blue-400"    },
  late:    { bg: "bg-orange-500/15 border-orange-500/30", text: "text-orange-400"  },
  other:   { bg: "bg-white/5 border-white/10",            text: "text-white/30"    },
};

const LEGEND = [
  { key: "present", label: "Present" },
  { key: "absent",  label: "Absent"  },
  { key: "leave",   label: "Leave"   },
  { key: "wfh",     label: "WFH"     },
  { key: "late",    label: "Late"    },
];

function normalizeStatus(s) {
  if (!s) return "other";
  const v = String(s).trim().toLowerCase();
  if (v === "present")              return "present";
  if (v === "absent")               return "absent";
  if (v === "leave" || v === "on leave") return "leave";
  if (v === "wfh" || v === "remote") return "wfh";
  if (v === "late")                 return "late";
  return "other";
}

export default function AttendanceCalendar({ onDateClick }) {
  const [calendarData, setCalendarData] = useState({});
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }

    setLoading(true);
    setError(null);

    fetch(`${API_BASE}/api/attendance/calendar?month=${currentMonth.format("YYYY-MM")}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(list => {
        const map = {};
        (Array.isArray(list) ? list : []).forEach(r => {
          if (!r?.date) return;
          map[String(r.date).slice(0, 10)] = normalizeStatus(r.status);
        });
        setCalendarData(map);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentMonth]);

  const days = (() => {
    const result = [];
    const startDay = currentMonth.startOf("month").day();
    const total    = currentMonth.daysInMonth();
    for (let i = 0; i < startDay; i++) result.push(null);
    for (let d = 1; d <= total; d++) {
      const dateStr = currentMonth.date(d).format("YYYY-MM-DD");
      result.push({ date: dateStr, day: d, status: calendarData[dateStr] || "other" });
    }
    return result;
  })();

  const today = dayjs().format("YYYY-MM-DD");

  return (
    <div className="rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022] p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
          {currentMonth.format("MMMM YYYY")}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentMonth(m => m.subtract(1, "month"))}
            className="w-8 h-8 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:text-[#00ff90] hover:border-[#00ff90]/40 transition-colors flex items-center justify-center text-sm"
          >←</button>
          <button
            onClick={() => setCurrentMonth(dayjs())}
            className="px-3 h-8 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:text-[#00ff90] hover:border-[#00ff90]/40 transition-colors text-xs"
          >Today</button>
          <button
            onClick={() => setCurrentMonth(m => m.add(1, "month"))}
            className="w-8 h-8 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:text-[#00ff90] hover:border-[#00ff90]/40 transition-colors flex items-center justify-center text-sm"
          >→</button>
        </div>
      </div>

      {error && <p className="text-red-400 text-sm mb-3">⚠ {error}</p>}

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
          <div key={d} className="text-center text-xs text-[#00ff90]/30 py-1">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      {loading ? (
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, idx) => {
            if (!day) return <div key={idx} />;
            const style = STATUS_STYLE[day.status] || STATUS_STYLE.other;
            const isToday = day.date === today;
            return (
              <button
                key={idx}
                onClick={() => onDateClick?.(day.date)}
                className={`h-12 rounded-lg border flex flex-col items-center justify-center gap-0.5 transition-all hover:scale-105 ${style.bg} ${isToday ? "ring-2 ring-[#00ff90]/60 ring-offset-1 ring-offset-[#0d0d0d]" : ""}`}
              >
                <span className={`text-sm font-medium ${isToday ? "text-[#00ff90]" : style.text}`}>
                  {day.day}
                </span>
                {day.status !== "other" && (
                  <span className={`text-[9px] uppercase tracking-wider ${style.text} opacity-70`}>
                    {day.status}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="flex gap-4 flex-wrap mt-4 pt-3 border-t border-white/5">
        {LEGEND.map(({ key, label }) => {
          const style = STATUS_STYLE[key];
          return (
            <div key={key} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-sm border ${style.bg}`} />
              <span className="text-xs text-white/30">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}